/* ─── graphLayout ────────────────────────────────────────────────────
   Placement des nœuds du graphe d'atelier.

   On n'utilise PAS un layout de DAG générique (dagre) : un atelier n'est
   pas un graphe quelconque, c'est une colonne vertébrale linéaire (la
   `pile`) sur laquelle se greffent des branches locales. Le rendre à plat
   détruit précisément l'information qu'on veut lire.

   Principe :
   - TRONC  : les pages de la pile, colonne fixe à gauche, ordre = ordre de
              jeu, enchaînées verticalement (bas d'une carte → haut de la
              suivante). Se lit comme un script.
   - COULOIRS : chaque conséquence est placée dans la colonne correspondant
              à sa profondeur (1, 2, …), sous son parent, en descendant un
              curseur par couloir. Un couloir ne remonte jamais.

   Conséquence importante : le layout est STABLE. Ajouter une conséquence
   allonge un couloir au lieu de rebattre tout le graphe, et le tronc ne
   bouge jamais quand on plie/déplie une branche.

   Deux mécanismes réduisent le "mur de conséquences" :
   - ABSORPTION : une carte de décisions dépliée affiche ses conséquences
     terminales dans un cadre aligné sous chaque mesure. Ces pages ne sont
     alors plus des nœuds du canvas — l'attribution se lit par la position
     de la colonne, sans tracer une seule arête.
   - REGROUPEMENT : hors dépliage, les feuilles nombreuses d'un même nœud
     fusionnent en un nœud repliable.

   Une conséquence qui ENCHAÎNE sur autre chose n'est jamais absorbée ni
   regroupée : la contenir dans une carte masquerait la suite du déroulé.
*/

import { PAGE_HANDLE } from "./graphBuilder";

export const NODE_W = 300;            // largeur d'une carte compacte
export const COL_W = 178;             // largeur d'une colonne de mesure (carte dépliée)
export const CARD_PAD_X = 14;
export const SPINE_X = 0;
export const LANE_GAP_X = 70;         // écart horizontal entre couloirs
export const LANE_W = NODE_W + LANE_GAP_X;
export const SPINE_GAP = 56;          // écart vertical dans le tronc (place pour le « + »)
export const LANE_GAP = 24;

export const LEAF_GROUP_THRESHOLD = 3;
export const GROUP_PREVIEW_ITEMS = 3;

/* Hauteurs — doivent rester cohérentes avec builder.css, sinon les cartes
   se chevauchent verticalement dans les couloirs. */
const H_PAD = 22;
const H_HEADER = 26;
const H_META = 22;
const H_ROW = 24;
const H_INERT = 22;
const H_EXP_HEADCELL = 46;            // titre de mesure (2 lignes max)
const H_EXP_PORTS = 22;
const H_EXP_LABEL = 22;               // bandeau « Conséquences »
const H_EXP_ITEM = 22;
const H_EXP_CELL_PAD = 14;
const H_GROUP_HEADER = 24;
const H_GROUP_ITEM = 22;

/** Une carte peut-elle passer en vue dépliée (tableau mesures/conséquences) ? */
export function isExpandable(node) {
  return !!node.hasDecisions && (node.rows || []).length > 0;
}

/** Une conséquence est-elle « contenable » dans une carte / un groupe ? */
function isLeafTarget(target) {
  return !!target && !target.missing && target.outgoingCount === 0 && !target.hasDecisions;
}

/**
 * Lignes de décision affichées sur une carte COMPACTE.
 * On ne montre que les mesures qui branchent : les seules qui portent de
 * l'information dans une vue de flux. Les autres sont repliées derrière un
 * compteur qui, au clic, déplie la carte entière.
 */
export function visibleRowsFor(node, expanded) {
  const rows = node.rows || [];
  if (expanded) return { rows, inertCount: 0 };
  const active = rows.filter((r) => r.takenCount + r.notTakenCount > 0 || r.missing);
  return { rows: active, inertCount: rows.length - active.length };
}

/** Nombre maximum de conséquences absorbées sur une colonne de la carte dépliée. */
function maxAbsorbedPerColumn(node, nodeById) {
  let max = 1;
  (node.rows || []).forEach((r) => {
    const count = [...r.taken, ...r.notTaken]
      .filter((t) => isLeafTarget(nodeById.get(t.targetNodeId))).length;
    if (count > max) max = count;
  });
  return max;
}

export function measureNode(node, { expanded = false, detail = 1, nodeById = new Map() } = {}) {
  if (node.kind === "group") {
    const shown = Math.min(node.items.length, GROUP_PREVIEW_ITEMS);
    const more = node.items.length > GROUP_PREVIEW_ITEMS ? H_GROUP_ITEM : 0;
    return { width: NODE_W, height: H_PAD + H_GROUP_HEADER + shown * H_GROUP_ITEM + more };
  }

  const compactBase = H_PAD + H_HEADER + H_META;
  if (node.missing) return { width: NODE_W, height: compactBase };
  if (detail === 0) return { width: NODE_W, height: compactBase };

  const isChip = !node.hasDecisions && node.outgoingCount === 0 && !node.inPile;
  if (isChip) return { width: NODE_W, height: compactBase };

  if (expanded && isExpandable(node)) {
    const columns = node.rows.length;
    return {
      width: Math.max(NODE_W, columns * COL_W + 2 * CARD_PAD_X),
      height: compactBase + H_EXP_HEADCELL + H_EXP_PORTS + H_EXP_LABEL
        + maxAbsorbedPerColumn(node, nodeById) * H_EXP_ITEM + H_EXP_CELL_PAD,
    };
  }

  let height = compactBase;
  if (node.hasDecisions) {
    const { rows, inertCount } = visibleRowsFor(node, false);
    height += rows.length * H_ROW;
    if (inertCount > 0) height += H_INERT;
  }
  return { width: NODE_W, height };
}

/**
 * Retire du canvas les conséquences terminales des cartes dépliées : elles
 * sont rendues DANS la carte, alignées sous leur mesure.
 */
function absorbIntoExpandedCards(nodes, edges, expandedCards) {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const hidden = new Set();
  const removedEdges = new Set();

  edges.forEach((e) => {
    if (!e.decisionId) return;                       // seules les conséquences de mesure
    if (!expandedCards.has(e.source)) return;
    if (!isLeafTarget(nodeById.get(e.target))) return;  // une suite ne s'absorbe pas
    hidden.add(e.target);
    removedEdges.add(e.id);
  });

  return { hidden, edges: edges.filter((e) => !removedEdges.has(e.id)) };
}

/**
 * Regroupe les conséquences terminales d'un même nœud source (hors cartes
 * dépliées, qui les ont déjà absorbées).
 */
function groupLeaves(nodes, edges, expandedGroups, alreadyHidden) {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  const leafEdgesBySource = new Map();
  edges.forEach((e) => {
    if (e.kind === "sequence") return;
    if (alreadyHidden.has(e.target)) return;
    if (!isLeafTarget(nodeById.get(e.target))) return;
    if (!leafEdgesBySource.has(e.source)) leafEdgesBySource.set(e.source, []);
    leafEdgesBySource.get(e.source).push(e);
  });

  const groupNodes = [];
  const hiddenNodeIds = new Set();
  const removedEdgeIds = new Set();

  leafEdgesBySource.forEach((leafEdges, sourceId) => {
    if (leafEdges.length < LEAF_GROUP_THRESHOLD) return;
    const groupId = `grp::${sourceId}`;
    const expanded = expandedGroups.has(groupId);
    const source = nodeById.get(sourceId);

    groupNodes.push({
      kind: "group",
      id: groupId,
      sourceNodeId: sourceId,
      depth: (source?.depth ?? 0) + 1,
      parentId: sourceId,
      expanded,
      items: leafEdges.map((e) => ({ edge: e, target: nodeById.get(e.target) })),
      inPile: false,
      missing: false,
      rows: [],
      hasDecisions: false,
      outgoingCount: 0,
    });

    if (!expanded) {
      leafEdges.forEach((e) => {
        removedEdgeIds.add(e.id);
        hiddenNodeIds.add(e.target);
      });
    }
  });

  const nextEdges = edges.filter((e) => !removedEdgeIds.has(e.id));
  groupNodes.forEach((g) => {
    if (g.expanded) return;
    nextEdges.push({
      id: `grpedge::${g.id}`,
      source: g.sourceNodeId,
      target: g.id,
      sourceHandle: PAGE_HANDLE,
      kind: "group",
      label: null,
      conditional: false,
      missing: false,
      groupSize: g.items.length,
      ref: null,
    });
  });

  return { groupNodes, hiddenNodeIds, edges: nextEdges };
}

/**
 * layoutGraph(graph, options)
 * → { nodes: [...{ ...node, position, width, height, autoPosition }], edges }
 *
 * options :
 *   expandedCards  : Set<nodeId>          cartes en vue tableau
 *   expandedGroups : Set<groupId>         groupes de feuilles dépliés
 *   manualPositions: { [nodeId]: {x,y} }  déplacements manuels de l'auteur
 *   detail         : 0 | 1 | 2            niveau de zoom sémantique
 */
export function layoutGraph(graph, options = {}) {
  const {
    expandedCards = new Set(),
    expandedGroups = new Set(),
    manualPositions = {},
    detail = 1,
  } = options;

  const absorbed = absorbIntoExpandedCards(graph.nodes, graph.edges, expandedCards);
  const grouped = groupLeaves(graph.nodes, absorbed.edges, expandedGroups, absorbed.hidden);

  const hiddenNodeIds = new Set([...absorbed.hidden, ...grouped.hiddenNodeIds]);
  const visibleNodes = [
    ...graph.nodes.filter((n) => !hiddenNodeIds.has(n.id)),
    ...grouped.groupNodes,
  ];
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]));
  const visibleById = new Map(visibleNodes.map((n) => [n.id, n]));
  const edges = grouped.edges.filter((e) => !hiddenNodeIds.has(e.source) && !hiddenNodeIds.has(e.target));

  const sizes = new Map(
    visibleNodes.map((n) => [n.id, measureNode(n, { expanded: expandedCards.has(n.id), detail, nodeById })])
  );
  const sizeOf = (id) => sizes.get(id) || { width: NODE_W, height: 80 };

  // Les couloirs commencent après la carte de tronc la plus large : déplier une
  // mosaïque ne doit jamais la faire chevaucher ses propres conséquences.
  const widestSpine = visibleNodes
    .filter((n) => n.inPile)
    .reduce((max, n) => Math.max(max, sizeOf(n.id).width), NODE_W);
  const laneX0 = SPINE_X + widestSpine + LANE_GAP_X;
  const laneX = (depth) => laneX0 + (depth - 1) * LANE_W;

  const childrenOf = new Map(visibleNodes.map((n) => [n.id, []]));
  edges.forEach((e) => {
    if (e.kind === "sequence") return;
    childrenOf.get(e.source)?.push(e.target);
  });

  const positions = new Map();
  const laneCursor = [];
  const cursorFor = (depth) => laneCursor[depth] ?? 0;

  const placeSubtree = (nodeId, parentY, seen) => {
    if (seen.has(nodeId)) return;
    seen.add(nodeId);
    const node = visibleById.get(nodeId);
    if (!node) return;

    const depth = Math.max(1, node.depth);
    const y = Math.max(cursorFor(depth), parentY);
    positions.set(nodeId, { x: laneX(depth), y });
    laneCursor[depth] = y + sizeOf(nodeId).height + LANE_GAP;

    (childrenOf.get(nodeId) || []).forEach((childId) => placeSubtree(childId, y, seen));
  };

  const seen = new Set();
  let spineY = 0;
  visibleNodes
    .filter((n) => n.inPile)
    .sort((a, b) => (a.pileIndex ?? 0) - (b.pileIndex ?? 0))
    .forEach((spineNode) => {
      positions.set(spineNode.id, { x: SPINE_X, y: spineY });
      seen.add(spineNode.id);
      (childrenOf.get(spineNode.id) || []).forEach((childId) => placeSubtree(childId, spineY, seen));
      spineY += sizeOf(spineNode.id).height + SPINE_GAP;
    });

  // Sécurité : ne jamais perdre un nœud (orphelin, cycle refermé…).
  visibleNodes.forEach((n) => {
    if (positions.has(n.id)) return;
    const depth = Math.max(1, n.depth);
    const y = cursorFor(depth);
    positions.set(n.id, { x: laneX(depth), y });
    laneCursor[depth] = y + sizeOf(n.id).height + LANE_GAP;
  });

  const positioned = visibleNodes.map((n) => {
    const size = sizeOf(n.id);
    return {
      ...n,
      width: size.width,
      height: size.height,
      expanded: expandedCards.has(n.id),
      position: manualPositions[n.id] || positions.get(n.id),
      autoPosition: positions.get(n.id),
      moved: !!manualPositions[n.id],
    };
  });

  return { nodes: positioned, edges, laneX0 };
}
